import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import type { Debtor } from "@/types";
import api from "@/services/api";

export interface DebtorFilter {
  bank_name?: string;
  region_code?: string;
  status?: Debtor["status"];
  searchTerm?: string;
  agent?: string;
  loan_type?: string;
}

export interface DebtorSort {
  field: keyof Debtor | null;
  direction: "asc" | "desc";
}

interface DebtorsState {
  items: Debtor[];
  loading: boolean;
  error: string | null;
  filter: DebtorFilter;
  sort: DebtorSort;
  currentPage: number;
  pageSize: number;
  total: number;
}

const initialState: DebtorsState = {
  items: [],
  loading: false,
  error: null,
  filter: {},
  sort: { field: null, direction: "asc" },
  currentPage: 1,
  pageSize: 10,
  total: 0,
};

// Async thunk for fetching debtors
export const fetchDebtors = createAsyncThunk(
  "debtors/fetchDebtors",
  async (params: { page?: number; per_page?: number; searchTerm?: string } = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { debtors: DebtorsState };
      const { currentPage, pageSize, filter } = state.debtors;
      
      const queryParams = new URLSearchParams({
        page: String(params?.page ?? currentPage),
        per_page: String(params?.per_page ?? pageSize),
      });

      // Add search parameter
      const searchTerm = params?.searchTerm ?? filter.searchTerm;
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }

      // Add filter parameters
      if (filter.bank_name) {
        queryParams.append('bank_name', filter.bank_name);
      }
      if (filter.region_code) {
        queryParams.append('region_code', filter.region_code);
      }
      if (filter.status) {
        queryParams.append('status', filter.status);
      }
      if (filter.agent) {
        queryParams.append('agent', filter.agent);
      }
      if (filter.loan_type) {
        queryParams.append('loan_type', filter.loan_type);
      }

      const response = await api.get<{
        data: Debtor[];
        meta: {
          current_page: number;
          per_page: number;
          total: number;
          last_page: number;
          from: number | null;
          to: number | null;
        };
      }>(`/v1/debtors?${queryParams}`);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch debtors",
      );
    }
  },
);

const debtorsSlice = createSlice({
  name: "debtors",
  initialState,
  reducers: {
    setDebtorFilter: (state, action: PayloadAction<DebtorFilter>) => {
      state.filter = action.payload;
      state.currentPage = 1; // Reset to first page on filter change
    },
    setDebtorSort: (state, action: PayloadAction<DebtorSort>) => {
      state.sort = action.payload;
      state.currentPage = 1; // Reset to first page on sort change
    },
    setDebtorPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setDebtorPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    resetDebtorFilters: (state) => {
      state.filter = {};
      state.sort = { field: null, direction: "asc" };
      state.currentPage = 1;
    },
    addDebtor: (state, action: PayloadAction<Debtor>) => {
      state.items.unshift(action.payload);
      state.total += 1;
    },
    updateDebtor: (state, action) => {
      const index = state.items.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deleteDebtor: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((d) => d.id !== action.payload);
      state.total -= 1;
    },
    resetDebtors: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDebtors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDebtors.fulfilled, (state, action) => {
        state.loading = false;
        state.items = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.total = action.payload.meta?.total ?? 0;
        state.currentPage = action.payload.meta?.current_page ?? 1;
        state.pageSize = action.payload.meta?.per_page ?? state.pageSize;
        state.error = null;
      })
      .addCase(fetchDebtors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setDebtorFilter,
  setDebtorSort,
  setDebtorPage,
  setDebtorPageSize,
  resetDebtorFilters,
  addDebtor,
  updateDebtor,
  deleteDebtor,
  resetDebtors,
} = debtorsSlice.actions;
export default debtorsSlice.reducer;

// Selectors
export const selectDebtors = (state: any) => state.debtors.items;
export const selectDebtorsLoading = (state: any) => state.debtors.loading;
export const selectDebtorsError = (state: any) => state.debtors.error;
export const selectDebtorFilter = (state: any) => state.debtors.filter;
export const selectDebtorSort = (state: any) => state.debtors.sort;
export const selectDebtorPagination = (state: any) => ({
  currentPage: state.debtors.currentPage,
  pageSize: state.debtors.pageSize,
  total: state.debtors.total,
});

// Filtration and sorting logic
const selectDebtorsState = (state: any) => state.debtors;
const selectDebtorsItems = createSelector(
  [(state: any) => state.debtors.items],
  (items) => items,
);
const selectDebtorsFilter = createSelector(
  [(state: any) => state.debtors.filter],
  (filter) => filter,
);
const selectDebtorsSort = createSelector(
  [(state: any) => state.debtors.sort],
  (sort) => sort,
);
const selectDebtorsPagination = createSelector(
  [
    (state: any) => state.debtors.currentPage,
    (state: any) => state.debtors.pageSize,
  ],
  (currentPage, pageSize) => ({ currentPage, pageSize }),
);

export const selectFilteredDebtors = createSelector(
  [selectDebtorsState],
  (state) => ({
    data: state.items,
    total: state.total,
    currentPage: state.currentPage,
    pageSize: state.pageSize,
    hasNextPage: state.currentPage < Math.ceil(state.total / state.pageSize),
    hasPrevPage: state.currentPage > 1,
  }),
);